import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

// Procedural image-based lighting for r3f canvases. Replaces drei's
// <Environment preset="...">, which fetches an HDR from raw.githack.com at
// runtime — the production CSP (connect-src) blocks that origin, the
// loader's error propagated through Suspense, and entire viewers crashed
// into their error boundaries (FloorPlanViewer on every room; Model3DViewer
// for every GLB). three's RoomEnvironment is generated on the GPU: no
// network, no bundled asset, CSP-immune, and it delivers the even neutral
// irradiance that makes white walls read white and gives models soft
// reflections.
//
// Wrapped in try/catch because lighting is decorative — if PMREM generation
// ever fails, the scene degrades to the viewer's ambient+directional lights
// instead of taking down the canvas.
//
// Null-rendering child: mount it anywhere inside a <Canvas>.
const SceneEnvironment = () => {
    const { gl, scene } = useThree();

    useEffect(() => {
        // No renderer/scene (e.g. the mocked Canvas in unit tests) —
        // nothing to light, nothing to clean up.
        if (!gl || !scene) return undefined;
        let envMap;
        let pmrem;
        try {
            pmrem = new THREE.PMREMGenerator(gl);
            const room = new RoomEnvironment();
            envMap = pmrem.fromScene(room, 0.04).texture;
            scene.environment = envMap;
        } catch (e) {
            console.warn('SceneEnvironment: failed to build environment lighting', e);
        }
        return () => {
            scene.environment = null;
            if (envMap) envMap.dispose();
            if (pmrem) pmrem.dispose();
        };
    }, [gl, scene]);

    return null;
};

export default SceneEnvironment;
